import { StyleSheet, Text, View, ActivityIndicator, Image, ScrollView, Platform } from 'react-native'
import React, { useEffect, useRef, useState } from 'react'
import { Theme, ThemedText, ThemedView } from '@app/theme'
import ScreenWrapper from '@app/hoc/ScreenWrapper'
import CommonHeader from '@app/sharedComponents/Header/Header'
import { PrivateStackParamList } from '@app/navigationApp/types'
import { NativeStackScreenProps } from '@react-navigation/native-stack'
import Stepper from '@app/sharedComponents/Stepper/Stepper'
import { useStepperData } from '@app/hooks/useStepperData'
import Button from '@app/sharedComponents/Button/Button'
import { useTheme } from '@shopify/restyle'
import CheckBox from '@app/sharedComponents/CheckBox/CheckBox'
import TataLogo from '@app/assets/svg/TataLogo'
import AlertIcon from '@app/assets/svg/AlertIcon'
import { checkEsignStatusTataApi, esignInitApi, initKfsTataApi, pollKfsTataApi, validateEsignApi } from './tataEsignService'
import { useAppDispatch, useAppSelector } from '@app/store/hooks'
import TataEsignProgress from './Components/TataEsignProgress'
import { signDeskSDKUrl } from '@app/api/apis'
import BottomSheetContainer from '@app/sharedComponents/Bottomsheet/Bottomsheet'
import TataSignErrorModal from './Components/TataSignErrorModal'
import TataSignSuccessModal from './Components/TataSignSuccessModal'
import WebView from 'react-native-webview'
import { updateLinkedApplication } from '@app/store/slices/authSlice'
import { getNextRoute } from '@app/navigationApp/navigationUtil'
import { updatePartnerParams } from '@app/store/slices/platformSlice'
import { SvgUri } from 'react-native-svg';
import TataEsignWebview from './Components/TataEsignWebview'
import handleApiErr from '@app/helper/handleApiErr'
import { showErrorSheet } from '@app/store/slices/errorSlice'

type ScreenProps = NativeStackScreenProps<PrivateStackParamList, 'SIGN_DESK_ESIGN'>

const TataESign = ({ navigation, route }: ScreenProps) => {

    const userDetails = useAppSelector((state) => state?.auth?.userDetails);
    const dispatch = useAppDispatch();

    const pan = userDetails?.linkedBorrowerAccounts[0].accountHolderPAN

    const timerRef = useRef<NodeJS.Timeout | null>(null);


    const { stepperData, isLoading } = useStepperData();
    const { colors } = useTheme<Theme>();

    const [isChecked, setIsChecked] = useState(true);
    const [isSigning, setIsSigning] = useState(false);
    const [modalState, setModalState] = useState<'success' | 'error' | 'idle' | 'esign_mobile'>('idle');
    const [kfsUrl, setKfsUrl] = useState<string | null>(null);

    const [eSignResponse, setESignResponse] = useState<any>(null);

    const [isPolling, setIsPolling] = useState(false);

    useEffect(() => {
        dispatch(updatePartnerParams({ showCall: true }))
    }, [])

    useEffect(() => {
        initKfsTataApi({
            applicationId: userDetails?.linkedApplications[0]?.applicationId,
        });

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    const handleContact = () => {

        setModalState('idle');
        setTimeout(() => {
            navigation.navigate('CONTACT_US');
        }, 500);
    }

    const handleRetry = () => {
        setModalState('idle');
    }


    const pollKfsTata = async () => {
        setIsPolling(true);
        let count = 0;
        let gotKfsUrl = false;
        let response = await pollKfsTataApi({
            applicationId: userDetails?.linkedApplications[0]?.applicationId,
        });
        if (response.data.stepResponseObject.status === 'COMPLETED') {
            gotKfsUrl = true;
            setIsPolling(false);
            setKfsUrl(response?.data?.stepResponseObject?.s3Url);
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        } else if (response.data.stepResponseObject.status === 'FAILED') {
            setIsPolling(false);
            setModalState('error');
        }

        if (!gotKfsUrl) {
            timerRef.current = setInterval(async () => {
                try {
                    response = await pollKfsTataApi({
                        applicationId: userDetails?.linkedApplications[0]?.applicationId,
                    });

                    if (response.data.stepResponseObject.status === 'COMPLETED') {
                        setIsPolling(false);
                        setKfsUrl(response?.data?.stepResponseObject?.s3Url);
                        if (timerRef.current) {
                            clearInterval(timerRef.current);
                        }
                    } else if (response.data.stepResponseObject.status === 'FAILED') {
                        setIsPolling(false);
                        setModalState('error');
                    }
                    count++;
                    if (count > 10) {
                        setIsPolling(false);
                        if (timerRef.current) {
                            clearInterval(timerRef.current);
                        }
                    }
                } catch (error) {
                    setIsPolling(false);
                }
            }, 3000);
        }
    }


    const handleContinue = async () => {
        setIsSigning(true);
        try {
            const response = await esignInitApi({
                applicationId: userDetails?.linkedApplications[0]?.applicationId,
            });
            if (Platform.OS === 'web') {
                const script = document.createElement('script')
                script.src = signDeskSDKUrl
                document.body.appendChild(script)

                let options = {
                    id: response?.data?.stepResponseObject?.applicationId,
                    docket_id: response?.data?.stepResponseObject?.docketId,
                    signer_id: response?.data?.stepResponseObject?.signerIds[0],
                    document_id: response?.data?.stepResponseObject?.documentId,
                    //optional
                    getWidgetToken: async (result: any) => {
                        const response = await validateEsignApi({
                            applicationId: userDetails?.linkedApplications[0]?.applicationId,
                            widgetToken: result,
                        });
                    },

                    signatureStatus: async (result: any) => {
                        if (result.status === 'failed') {
                            setIsSigning(false);
                            setKfsUrl(null);
                            setTimeout(() => {
                                setModalState('error');
                            }, 500);
                        } else {

                            try {
                                const response = await checkEsignStatusTataApi({
                                    applicationId: userDetails?.linkedApplications[0]?.applicationId,
                                });

                                if (response?.status === 200) {
                                    if (Platform.OS === "web") {
                                        //@ts-ignore
                                        window?.ReactNativeWebView?.postMessage("LOAN_CREATED")
                                    }
                                    dispatch(updateLinkedApplication(response?.data?.updatedApplicationObj))
                                    setIsSigning(false);
                                    setKfsUrl(null);
                                    setTimeout(() => {
                                        setModalState('success');
                                    }, 500);
                                }
                            } catch (error) {
                                console.log('error', error);
                            }

                        }
                    },
                    errorLog: async () => { },
                }



                setTimeout(() => {
                    // setIsSigning(false);
                    const esignWidgetCall = (window as any).esignWidgetCall;

                    esignWidgetCall(options);
                }, 2000);
            } else {
                
                setIsSigning(false);
                setESignResponse(response);
            }
        }
        catch (error) {
            setIsSigning(false);
            console.log('error', error);
        }

    }

    const onMobileEsignSuccess = async (status: string) => {
        setESignResponse(false);
        if (status === 'failed') {
            setTimeout(() => {
                setModalState('error')
            }, 600)
        } else {
            try {
                const response = await checkEsignStatusTataApi({
                    applicationId: userDetails?.linkedApplications[0]?.applicationId,
                });
                const updatedApplicationObj = response?.data?.updatedApplicationObj;
                dispatch(updateLinkedApplication(updatedApplicationObj));
                setModalState('success');
            } catch (error: any) {
                if (handleApiErr(error)) {
                    dispatch(showErrorSheet({
                        isErrorSheetVisible: true,
                        errorMessage: error?.data?.message,
                        closeOnBackdropPress: true,
                        errorType: error?.data?.voltErrorCode || error?.data?.statusCode,
                        showErrorHeading: true,
                        errorHeading: error?.data?.header,
                        btnName: 'Go back',
                    }))
                }
            }

        }
    }




    const onSuccess = () => {
        setModalState('idle');
        navigation.reset({
            index: 0,
            routes: [{ name: 'KYC_STEPPER' }, { name: getNextRoute(userDetails?.linkedApplications[0].currentStepId, userDetails?.linkedApplications[0].applicationState, userDetails?.linkedApplications[0].stepStatusMap) }]
        })
    }


    if (isSigning) {
        return <TataEsignProgress />
    }

    if (eSignResponse) {
        return <TataEsignWebview onSuccess={onMobileEsignSuccess} eSignResponse={eSignResponse} />
    }




    if (isLoading) {
        return <ThemedView flex={1} bg='bg-surface-primary'>
            <ThemedView flex={1} justifyContent='center' alignItems='center'>
                <ActivityIndicator size={12} color={'#1434CB'} />
            </ThemedView>
        </ThemedView>
    }


    return (
        <ScreenWrapper>
            <ThemedView flex={1} bg={'bg-surface-primary'}>
                <CommonHeader label='Review Agreement' navigation={navigation} hideBottomBorder pageName={route.name} />

                {isPolling ? (
                    <ThemedView flex={1} justifyContent='center' alignItems='center'>
                        <ActivityIndicator size={12} color={'#1434CB'} />
                        <ThemedText mt='md' variant='b4r' color='text-primary'>Loading KFS...</ThemedText>
                    </ThemedView>
                ) : kfsUrl ? (
                    <WebView style={{ flex: 1 }} source={{ uri: kfsUrl }} />
                ) : (
                    <>
                        <Stepper stepperData={stepperData} />
                        <ScrollView>
                            <ThemedView padding='lg' paddingTop='2xl'>
                                {Platform.OS === 'web' ? (
                                    <Image
                                        source={{ uri: 'https://volt-images-alpha.s3.ap-south-1.amazonaws.com/loan-agreement.svg' }}
                                        style={{ width: 64, height: 64 }}
                                    />
                                ) : (
                                    <SvgUri width={64} height={64} uri={'https://volt-images-alpha.s3.ap-south-1.amazonaws.com/loan-agreement.svg'} />
                                )}
                                <ThemedText mt='md' variant='h5s' color='text-primary'>
                                    Proceed to sign agreement
                                </ThemedText>
                                <ThemedText variant='b3r' color='text-secondary' mt='sm' mb='md'>Proceed to review & confirm the loan cum pledge agreement.
                                </ThemedText>
                            </ThemedView>
                        </ScrollView>
                        <ThemedView flexDirection='row' borderRadius='sm' bg='bg-surface-secondary' marginHorizontal='lg' p='md' mb='lg'>
                            <AlertIcon size={20} color={colors['icon-secondary']} />
                            <ThemedText ml='sm' variant='b4r' color='text-secondary'>You'll be redirected to our lending partner's site to review and sign the loan agreement.</ThemedText>
                        </ThemedView>
                    </>
                )}

                <ThemedView
                    bg='bg-surface-primary'
                    padding='lg'
                    borderTopWidth={1}
                    borderTopColor='border-primary'
                >
                    <ThemedView mb='xs' flexDirection='row' alignItems='center'>
                        <CheckBox
                            size={16}
                            onToggle={() => setIsChecked(!isChecked)}
                            isChecked={isChecked}
                        />
                        <ThemedText textAlign='left' ml='xs' color='text-primary' variant='b4r'>
                            I understand acknowledge and agree to terms in <ThemedText onPress={() => {
                                if (!kfsUrl) {
                                    pollKfsTata();
                                }
                            }} variant='b4r' color='text-action-primary'>Key fact statement</ThemedText>
                        </ThemedText>


                    </ThemedView>
                    <ThemedView marginVertical='md'>
                        <Button
                            label="Proceed to next step"
                            type="SOLID"
                            disabled={!isChecked}
                            size='MD'
                            buttonLoader={isSigning}
                            handlePress={handleContinue}
                            bg={colors['bg-action-primary']}
                            textColor={colors['text-primary']}
                        />
                    </ThemedView>
                    <ThemedView flexDirection='row' alignItems='center' justifyContent='center'>
                        <ThemedText mr='sm' variant='b4r' color='text-secondary'>LENDING PARTNER</ThemedText>
                        <TataLogo />
                    </ThemedView>
                </ThemedView>

            </ThemedView>

            <BottomSheetContainer
                isVisible={modalState !== 'idle'}
                close={() => setModalState('idle')}
                onClose={() => setModalState('idle')}
                dragComponent={<View style={{ width: 40, height: 4, backgroundColor: colors['border-primary'], borderRadius: 10 }} />}
                containerStyle={{ borderTopRightRadius: 16, borderTopLeftRadius: 16 }}
                closeOnBackdropPress={true}
                borderTopRightRadius={10}
                borderTopLeftRadius={10}
            >
                {modalState === 'error' && <TataSignErrorModal onRetry={handleRetry} onContact={handleContact} />}
                {modalState === 'success' && <TataSignSuccessModal onSuccess={onSuccess} />}
            </BottomSheetContainer>
        </ScreenWrapper>
    )
}

export default TataESign

const styles = StyleSheet.create({})